import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete, Box, Button, Chip, CircularProgress, IconButton, Stack,
  TextField, Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useGetData } from '../services/hooks/useGetData';
import useFetch from '../services/useFetch';
import PostDataRetData from '../services/PostDataRetData';
import DeleteData from '../services/DeleteData';
import PageHeader from '../components/layout/PageHeader';
import AppDialog from '../components/ui/AppDialog';
import SnackbarEx from '../components/Helpers/SnackbarEx';
import { tokens } from '../theme';

const baseUrl = process.env.REACT_APP_NETLIFY_API_URL || process.env.REACT_APP_API_URL;

function assignmentKey(a) {
  return a._id || `${a.kind}-${a.skuItemId || a.productItemId}`;
}

export default function Shelving() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: envelope, isLoading, refetch } = useGetData('shelves', {});
  const { data: skuData } = useFetch('api/all-skus', false);
  const { data: menuItems } = useFetch('api/menuItems', false);

  const shelves = envelope?.data || [];
  const [selectedId, setSelectedId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [shelfForm, setShelfForm] = useState({ name: '', code: '', notes: '' });
  const [assignKind, setAssignKind] = useState('product');
  const [assignItem, setAssignItem] = useState(null);
  const [assignQty, setAssignQty] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingQtyId, setEditingQtyId] = useState(null);
  const [editingQty, setEditingQty] = useState('');
  const [snack, setSnack] = useState({ open: false, color: '', message: '' });

  const selected = useMemo(
    () => shelves.find((s) => String(s._id) === String(selectedId)) || null,
    [shelves, selectedId],
  );

  useEffect(() => {
    if (!selectedId && shelves.length > 0) setSelectedId(shelves[0]._id);
    if (selectedId && shelves.length && !shelves.some((s) => String(s._id) === String(selectedId))) {
      setSelectedId(shelves[0]?._id || null);
    }
  }, [shelves, selectedId]);

  const skuOptions = useMemo(() => (
    (skuData || []).map((s) => ({
      id: s.itemId,
      label: s.itemName,
      meta: s.status || s.posOrSub,
    }))
  ), [skuData]);

  const productOptions = useMemo(() => (
    (menuItems || []).map((p) => ({
      id: p.itemId,
      posItemId: p.posItemId,
      label: p.variant && p.variant !== 'None' ? `${p.itemName} — ${p.variant}` : p.itemName,
      meta: `On hand ${p.availForSale ?? 0}`,
    }))
  ), [menuItems]);

  const productCount = (selected?.assignments || []).filter((a) => a.kind === 'product').length;
  const skuCount = (selected?.assignments || []).filter((a) => a.kind === 'sku').length;
  const shelfQtyTotal = (selected?.assignments || [])
    .filter((a) => a.kind === 'product')
    .reduce((sum, a) => sum + (Number(a.quantity) || 0), 0);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['shelves'] });
    await refetch();
  };

  const createShelf = async () => {
    if (!shelfForm.name.trim()) return;
    setSaving(true);
    const { data, error } = await PostDataRetData(
      { name: shelfForm.name.trim(), code: shelfForm.code, notes: shelfForm.notes },
      'api/shelves',
    );
    setSaving(false);
    if (error || data?.error) {
      setSnack({ open: true, color: 'error', message: data?.error?.message || 'Could not create shelf' });
      return;
    }
    setAddOpen(false);
    setShelfForm({ name: '', code: '', notes: '' });
    setSnack({ open: true, color: 'success', message: 'Shelf created' });
    await refresh();
    if (data?.data?._id) setSelectedId(data.data._id);
  };

  const removeShelf = async (shelfId) => {
    const { success } = await DeleteData(`api/shelves/${shelfId}`, {});
    if (!success) {
      setSnack({ open: true, color: 'error', message: 'Could not delete shelf' });
      return;
    }
    setSnack({ open: true, color: 'success', message: 'Shelf deleted' });
    if (String(selectedId) === String(shelfId)) setSelectedId(null);
    await refresh();
  };

  const addAssignment = async () => {
    if (!selected || !assignItem) return;
    if (assignKind === 'product' && (assignQty === '' || Number(assignQty) < 0 || Number.isNaN(Number(assignQty)))) {
      setSnack({ open: true, color: 'error', message: 'Enter a shelf quantity for the product' });
      return;
    }
    setSaving(true);
    const { data, error } = await PostDataRetData(
      {
        kind: assignKind,
        refId: assignItem.id,
        quantity: assignKind === 'product' ? Number(assignQty) : null,
      },
      `api/shelves/${selected._id}/assignments`,
    );
    setSaving(false);
    if (error || data?.error) {
      setSnack({
        open: true,
        color: 'error',
        message: data?.error?.message || error?.response?.data?.error?.message || 'Could not assign item',
      });
      return;
    }
    setAssignOpen(false);
    setAssignItem(null);
    setAssignQty('');
    setSnack({ open: true, color: 'success', message: 'Assigned to shelf' });
    await refresh();
  };

  const saveQty = async (assignmentId) => {
    if (!selected) return;
    const qty = Number(editingQty);
    if (!Number.isFinite(qty) || qty < 0) {
      setSnack({ open: true, color: 'error', message: 'Enter a valid quantity' });
      return;
    }
    const tkn = sessionStorage.getItem('urtkn');
    const location = localStorage.getItem('usrlctn');
    try {
      await axios.patch(
        `${baseUrl}/api/shelves/${selected._id}/assignments/${assignmentId}`,
        { location, data: { quantity: qty } },
        { headers: { Authorization: `Bearer ${tkn}` } },
      );
      setEditingQtyId(null);
      setSnack({ open: true, color: 'success', message: 'Quantity updated' });
      await refresh();
    } catch (err) {
      setSnack({
        open: true,
        color: 'error',
        message: err?.response?.data?.error?.message || 'Could not update quantity',
      });
    }
  };

  const removeAssignment = async (assignmentId) => {
    if (!selected) return;
    const tkn = sessionStorage.getItem('urtkn');
    const location = localStorage.getItem('usrlctn');
    try {
      await axios.delete(
        `${baseUrl}/api/shelves/${selected._id}/assignments/${assignmentId}`,
        { headers: { Authorization: `Bearer ${tkn}` }, params: { location } },
      );
      setSnack({ open: true, color: 'success', message: 'Removed from shelf' });
      await refresh();
    } catch (err) {
      setSnack({
        open: true,
        color: 'error',
        message: err?.response?.data?.error?.message || 'Could not remove assignment',
      });
    }
  };

  const openProduct = (assignment) => {
    const key = assignment.productPosItemId ?? assignment.productItemId;
    if (key == null) return;
    navigate(`/inventory/${key}`);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        title="Shelving"
        subtitle="Physical locations for product inventory and finished SKUs"
        actions={
          <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => setAddOpen(true)}>
            Add shelf
          </Button>
        }
      />

      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2.5,
          pb: 4,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '240px minmax(0, 1fr)' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        {/* Shelf list */}
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: tokens.inkMuted,
              fontWeight: 700,
              mb: 1.25,
              px: 0.5,
            }}
          >
            Locations
          </Typography>
          {isLoading && (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!isLoading && shelves.length === 0 && (
            <Typography sx={{ color: tokens.inkMuted, fontSize: 13, px: 0.5 }}>
              No shelves yet.
            </Typography>
          )}
          <Stack spacing={0.75}>
            {shelves.map((s) => {
              const active = String(s._id) === String(selectedId);
              const count = (s.assignments || []).length;
              return (
                <Box
                  key={s._id}
                  onClick={() => setSelectedId(s._id)}
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    cursor: 'pointer',
                    border: `1px solid ${active ? tokens.accent : 'transparent'}`,
                    backgroundColor: active ? 'rgba(91, 127, 94, 0.10)' : 'transparent',
                    '&:hover': {
                      backgroundColor: active ? 'rgba(91, 127, 94, 0.12)' : tokens.bg,
                    },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 650, fontSize: 14, color: tokens.ink, lineHeight: 1.3 }}>
                        {s.name}
                      </Typography>
                      <Typography sx={{ color: tokens.inkMuted, fontSize: 12, mt: 0.2 }}>
                        {s.code ? `${s.code} · ` : ''}{count} placed
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      aria-label="Delete shelf"
                      onClick={(e) => { e.stopPropagation(); removeShelf(s._id); }}
                      sx={{ color: tokens.inkMuted, opacity: 0.7, '&:hover': { opacity: 1, color: tokens.danger } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>

        {/* Detail */}
        <Box>
          {!selected ? (
            <Box
              sx={{
                py: 8,
                textAlign: 'center',
                borderTop: `1px solid ${tokens.border}`,
              }}
            >
              <Typography sx={{ color: tokens.inkMuted, fontSize: 14 }}>
                Select a shelf to manage what sits there.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'flex-end' }}
                gap={2}
                sx={{ pb: 2, borderBottom: `1px solid ${tokens.border}` }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "'Fraunces', 'Figtree', serif",
                      fontWeight: 650,
                      fontSize: 26,
                      letterSpacing: '-0.02em',
                      color: tokens.ink,
                    }}
                  >
                    {selected.name}
                  </Typography>
                  <Typography sx={{ color: tokens.inkMuted, fontSize: 13, mt: 0.5 }}>
                    {[
                      selected.code,
                      productCount ? `${productCount} product${productCount === 1 ? '' : 's'}` : null,
                      skuCount ? `${skuCount} SKU${skuCount === 1 ? '' : 's'}` : null,
                      productCount ? `${shelfQtyTotal} units on shelf` : null,
                      selected.notes,
                    ].filter(Boolean).join(' · ') || 'Empty shelf'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddOutlinedIcon />}
                  onClick={() => {
                    setAssignKind('product');
                    setAssignItem(null);
                    setAssignQty('');
                    setAssignOpen(true);
                  }}
                >
                  Assign
                </Button>
              </Stack>

              {(selected.assignments || []).length === 0 ? (
                <Typography sx={{ color: tokens.inkMuted, fontSize: 14, py: 4 }}>
                  Nothing on this shelf yet. Assign a product (with quantity) or a finished SKU.
                </Typography>
              ) : (
                <Box sx={{ mt: 1 }}>
                  {/* Column headers */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr auto', sm: '1fr 100px 88px 40px' },
                      gap: 1,
                      px: 0.5,
                      py: 1,
                      borderBottom: `1px solid ${tokens.border}`,
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.inkMuted }}>
                      Item
                    </Typography>
                    <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.inkMuted }}>
                      Type
                    </Typography>
                    <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.inkMuted, textAlign: 'right' }}>
                      Qty
                    </Typography>
                    <span />
                  </Box>

                  {(selected.assignments || []).map((a) => {
                    const isProduct = a.kind === 'product';
                    const isEditing = String(editingQtyId) === String(a._id);
                    return (
                      <Box
                        key={assignmentKey(a)}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr auto', sm: '1fr 100px 88px 40px' },
                          gap: 1,
                          alignItems: 'center',
                          px: 0.5,
                          py: 1.35,
                          borderBottom: `1px solid ${tokens.border}`,
                          '&:hover': { backgroundColor: 'rgba(243, 246, 241, 0.65)' },
                        }}
                      >
                        <Box
                          onClick={() => isProduct && openProduct(a)}
                          sx={{
                            minWidth: 0,
                            cursor: isProduct ? 'pointer' : 'default',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 650, fontSize: 14, color: isProduct ? tokens.accentHover : tokens.ink }}>
                              {a.label || 'Untitled'}
                            </Typography>
                            <Typography sx={{ color: tokens.inkMuted, fontSize: 12 }}>
                              {isProduct
                                ? (a.variantLabel || `Product #${a.productItemId}`)
                                : `SKU #${a.skuItemId}${a.variantLabel ? ` · ${a.variantLabel}` : ''}`}
                            </Typography>
                          </Box>
                          {isProduct && <ChevronRightIcon sx={{ fontSize: 18, color: tokens.inkMuted, flexShrink: 0 }} />}
                        </Box>

                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                          <Chip
                            size="small"
                            label={isProduct ? 'Product' : 'SKU'}
                            sx={{
                              height: 22,
                              fontWeight: 650,
                              fontSize: 11,
                              backgroundColor: isProduct ? tokens.okBg : tokens.warnBg,
                              color: isProduct ? tokens.accent : tokens.warn,
                            }}
                          />
                        </Box>

                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'flex-end' }}>
                          {isProduct ? (
                            isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingQty}
                                autoFocus
                                onChange={(e) => setEditingQty(e.target.value)}
                                onBlur={() => saveQty(a._id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') e.target.blur();
                                  if (e.key === 'Escape') setEditingQtyId(null);
                                }}
                                inputProps={{ min: 0, step: 'any', style: { textAlign: 'right', width: 64 } }}
                                sx={{
                                  '& .MuiOutlinedInput-root': { height: 32, fontWeight: 650 },
                                }}
                              />
                            ) : (
                              <Typography
                                onClick={() => {
                                  setEditingQtyId(a._id);
                                  setEditingQty(String(a.quantity ?? 0));
                                }}
                                sx={{
                                  fontWeight: 650,
                                  fontSize: 14,
                                  cursor: 'pointer',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  '&:hover': { backgroundColor: tokens.bg },
                                }}
                                title="Click to edit quantity"
                              >
                                {a.quantity ?? 0}
                              </Typography>
                            )
                          ) : (
                            <Typography sx={{ color: tokens.inkMuted, fontSize: 13 }}>—</Typography>
                          )}
                        </Box>

                        <IconButton
                          size="small"
                          aria-label="Remove"
                          onClick={() => removeAssignment(a._id)}
                          sx={{ color: tokens.inkMuted, justifySelf: 'end' }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <AppDialog
        open={addOpen}
        onClose={() => !saving && setAddOpen(false)}
        title="Add shelf"
        subtitle="Name a physical place inventory lives — cooler, dry storage, front rack."
        actions={
          <>
            <Button variant="outlined" onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={createShelf} disabled={saving || !shelfForm.name.trim()}>
              {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Create'}
            </Button>
          </>
        }
      >
        <Stack spacing={1.75}>
          <TextField
            label="Name"
            value={shelfForm.name}
            onChange={(e) => setShelfForm((p) => ({ ...p, name: e.target.value }))}
            size="small"
            fullWidth
            autoFocus
            placeholder="Walk-in cooler A"
          />
          <TextField
            label="Code"
            value={shelfForm.code}
            onChange={(e) => setShelfForm((p) => ({ ...p, code: e.target.value }))}
            size="small"
            fullWidth
            placeholder="Optional · WIC-A"
          />
          <TextField
            label="Notes"
            value={shelfForm.notes}
            onChange={(e) => setShelfForm((p) => ({ ...p, notes: e.target.value }))}
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder="Optional"
          />
        </Stack>
      </AppDialog>

      <AppDialog
        open={assignOpen}
        onClose={() => !saving && setAssignOpen(false)}
        title="Assign to shelf"
        subtitle={selected ? `What sits on “${selected.name}”?` : ''}
        actions={
          <>
            <Button variant="outlined" onClick={() => setAssignOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              variant="contained"
              onClick={addAssignment}
              disabled={
                saving
                || !assignItem
                || (assignKind === 'product' && assignQty === '')
              }
            >
              {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Assign'}
            </Button>
          </>
        }
      >
        <Stack spacing={1.75}>
          <Stack direction="row" spacing={1}>
            <Button
              variant={assignKind === 'product' ? 'contained' : 'outlined'}
              onClick={() => { setAssignKind('product'); setAssignItem(null); }}
              size="small"
            >
              Product
            </Button>
            <Button
              variant={assignKind === 'sku' ? 'contained' : 'outlined'}
              onClick={() => { setAssignKind('sku'); setAssignItem(null); setAssignQty(''); }}
              size="small"
            >
              SKU
            </Button>
          </Stack>
          <Autocomplete
            options={assignKind === 'sku' ? skuOptions : productOptions}
            value={assignItem}
            onChange={(_, val) => setAssignItem(val)}
            getOptionLabel={(o) => o?.label || ''}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{option.label}</Typography>
                  {option.meta && (
                    <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>{option.meta}</Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={assignKind === 'sku' ? 'SKU' : 'Product'}
                size="small"
                placeholder="Search…"
              />
            )}
          />
          {assignKind === 'product' && (
            <TextField
              label="Quantity on this shelf"
              type="number"
              size="small"
              required
              value={assignQty}
              onChange={(e) => setAssignQty(e.target.value)}
              inputProps={{ min: 0, step: 'any' }}
              helperText="Shown on the product page as shelf inventory"
            />
          )}
        </Stack>
      </AppDialog>

      {snack.open && (
        <SnackbarEx
          color={snack.color}
          message={snack.message}
          open={snack.open}
          close={() => setSnack({ open: false, color: '', message: '' })}
        />
      )}
    </Box>
  );
}
